import React from 'react';
import { LinkText } from '../../shared/Link';
import Text from '../../shared/Text';

const valueProps = [
  'Unlimited free hosting and distribution',
  'Upload your podcast or use our creation tools',
  'Take voice messages from fans and easily incorporate them into your podcast',
  'Track performance with cross-platform analytics',
  'Non-exclusive license allows you to own your content with no contracts',
  (() => (
    <Text size="lg" color="rgba(41, 47, 54, 0.6)">
      <LinkText isInline={true} to="/sponsorships">
        Monetization options
      </LinkText>{' '}
      you won’t find anywhere else
    </Text>
  ))(),
];

const stepsToSwitch = [
  {
    imageUrl:
      'https://d12xoj7p9moygp.cloudfront.net/images/screens/import/import.png',
    retinaImageUrl:
      'https://d12xoj7p9moygp.cloudfront.net/images/screens/import/import@2x.png',
    title: 'Import to Anchor',
    description:
      'It only takes a second to import your episodes; no manual uploading required.',
  },
  {
    imageUrl:
      'https://d12xoj7p9moygp.cloudfront.net/images/screens/import/redirect.png',
    retinaImageUrl:
      'https://d12xoj7p9moygp.cloudfront.net/images/screens/import/redirect@2x.png',
    title: 'Redirect your old feed',
    description:
      "This way, your listeners will be able to hear your new episodes. This won't impact your existing listing, reviews, or subscribers.",
  },
  {
    imageUrl:
      'https://d12xoj7p9moygp.cloudfront.net/images/screens/import/thats_it.png',
    retinaImageUrl:
      'https://d12xoj7p9moygp.cloudfront.net/images/screens/import/thats_it@2x.png',
    title: "That's it!",
    description:
      'Start making your future episodes with Anchor. Either upload your audio as-is or use our built-in tools to edit.',
  },
];

const faqs = [
  {
    question: 'Is Anchor really free? How is that possible?',
    renderAnswer: () => (
      <Text size="xl" align="left" color="#ffffff">
        We don’t believe in charging creators to store their work. Our platform
        helps podcasters monetize their work, and we take an industry-smallest
        cut of the revenue to make sure we can keep building the best product
        possible, for free.
      </Text>
    ),
  },
  {
    question: 'Who owns the rights to my content if I use Anchor?',
    renderAnswer: () => (
      <Text size="xl" align="left" color="#ffffff">
        You do, 100%. Creators on Anchor always have and always will own their
        content. If you’re curious about our license for hosting and
        distributing your podcast, full details can be found in our Terms of
        Service{' '}
        <LinkText to="/tos" isInline={true}>
          <Text size="xl" color="#ffffff" isInline={true} isBold={true}>
            here.
          </Text>
        </LinkText>
      </Text>
    ),
  },
  {
    question: 'How exactly does distribution work?',
    renderAnswer: () => (
      <div>
        <div>
          <Text size="xl" align="left" color="#ffffff">
            Anchor submits your RSS feed automatically to all major listening
            platforms, including Apple Podcasts, Google Podcasts, Spotify and{' '}
            <LinkText to="http://anch.co/distribution" isInline={true}>
              <Text size="xl" color="#ffffff" isInline={true} isBold={true}>
                many more
              </Text>
            </LinkText>
            . All it takes from you is one click; we handle everything else. For
            any platforms where you already have a presence, we&#39;ll link
            those accounts to make sure there are no duplicates and your fans
            won&#39;t see any changes.
          </Text>
        </div>
        <div style={{ marginTop: 20 }}>
          <Text size="xl" align="left" color="#ffffff">
            Prefer to manage your RSS feed and handle distribution yourself? No
            problem. Our One-Click Distribution is completely optional.
          </Text>
        </div>
      </div>
    ),
  },
  {
    question: 'What if I change my mind about switching to Anchor?',
    renderAnswer: () => (
      <Text size="xl" align="left" color="#ffffff">
        Importing your episodes to Anchor doesn&#39;t affect your RSS feed; the
        switch only happens when you redirect. So you&#39;ll have plenty of time
        to click around and make sure Anchor is the right platform for you
        before officially switching. And, if you find that Anchor isn&#39;t as
        amazing as you thought it would be, you can easily switch back at any
        time.
      </Text>
    ),
  },
  {
    question: 'Will my podcast be unavailable during the switching process?',
    renderAnswer: () => (
      <Text size="xl" align="left" color="#ffffff">
        There&#39;s zero down time during switching. Redirecting your feed from
        your old host to Anchor is instant, so you don&#39;t have to worry about
        anyone being unable to access your podcast. The whole process of
        switching will only take you a few minutes and it happens completely
        behind the scenes, so it won&#39;t have any impact on your listing or
        your subscribers.
      </Text>
    ),
  },
  {
    question:
      'Do I have to use Anchor’s creation tools or can I upload my own finished audio?',
    renderAnswer: () => (
      <Text size="xl" align="left" color="#ffffff">
        If you want to produce your show using another app, feel free to do so!
        You can upload your own audio to Anchor directly from our website or
        mobile apps and still take advantage of our free hosting, monetization,
        and more.
      </Text>
    ),
  },
  {
    question: 'Still have questions?',
    renderAnswer: () => (
      <Text size="xl" align="left" color="#ffffff">
        Get in touch with us{' '}
        <LinkText
          to="https://anchorhq.typeform.com/to/QzuiVt"
          target="_self"
          isInline={true}
        >
          <Text size="xl" color="#ffffff" isInline={true} isBold={true}>
            here
          </Text>
        </LinkText>
        . We look forward to hearing from you!
      </Text>
    ),
  },
];

const quotes = [
  {
    podcastCoverartImageUrl:
      'https://d12xoj7p9moygp.cloudfront.net/images/screens/import/journey_to_manifesting_podcast_art.png',
    podcastCoverartImageRetinaUrl:
      'https://d12xoj7p9moygp.cloudfront.net/images/screens/import/journey_to_manifesting_podcast_art@2x.png',
    authorName: 'Sarah Prout',
    podcastName: 'Journey to Manifesting',
    quote:
      "Anchor is the solution I've been waiting for! It removes so many steps in the podcast process and makes things super-easy. I am so thrilled with my choice to switch to Anchor.",
  },
  {
    podcastCoverartImageUrl:
      'https://d12xoj7p9moygp.cloudfront.net/images/screens/import/heartland_podcast_art.png',
    podcastCoverartImageRetinaUrl:
      'https://d12xoj7p9moygp.cloudfront.net/images/screens/import/heartland_podcast_art@2x.png',
    authorName: 'Ty Schmitt',
    podcastName: 'Heartland Radio 2.0',
    quote:
      'The thing that really surprised me was how intuitive and easy to use the platform is. I searched all over the internet for something that lets me post a podcast quickly without jumping through hoops.',
  },
  {
    podcastCoverartImageUrl:
      'https://d12xoj7p9moygp.cloudfront.net/images/screens/import/ask_women_podcast_art.png',
    podcastCoverartImageRetinaUrl:
      'https://d12xoj7p9moygp.cloudfront.net/images/screens/import/ask_women_podcast_art@2x.png',
    authorName: 'Marni Kinrys',
    podcastName: 'Ask Women Podcast',
    quote:
      'I’ve tried many other providers and they would charge me for every MB I loaded.  Anchor is free AND so easy to use AND great quality AND amazing customer service.  Why would I ever host my show anywhere else?',
  },
];

export { valueProps, stepsToSwitch, faqs, quotes };
